import { AccountTypeSuggestion, AccountTypeSuggestionResponse } from './types'

/**
 * Reads one answer, and refuses anything it cannot trust.
 *
 * The sibling of `readSuggestion` for account ids, with one difference: a type
 * is matched case-insensitively and then returned **as the repository spells
 * it**. The candidates come from the transaction settings ('trust', 'investment
 * account'); a model that answers "Trust" has picked the right one, and
 * correcting its capitalisation is cheaper than refusing a good answer — but the
 * value written to the form has to be the repository's, since that is what the
 * account will be created with.
 */
export const readTypeSuggestion = (
  data: string,
  candidates: string[],
): { suggestion?: AccountTypeSuggestion, error?: string } => {
  let answer: AccountTypeSuggestionResponse

  try {
    answer = JSON.parse(data) as AccountTypeSuggestionResponse
  } catch {
    return { error: 'The account type suggestion was not readable JSON' }
  }

  if (!answer || typeof answer !== 'object') {
    return { error: 'The account type suggestion was not an object' }
  }

  // The request does not say. There is simply no pill to mark.
  if (!answer.account_type) return {}

  const answered = answer.account_type.trim().toLowerCase()
  const offered = candidates.find(candidate => candidate.trim().toLowerCase() === answered)

  if (!offered) {
    return {
      error: `The account type suggestion named a type that was not offered: ${answer.account_type}`,
    }
  }

  const reason = answer.reason?.trim()
  if (!reason) {
    return { error: `The account type suggestion for ${offered} carried no reason` }
  }

  return { suggestion: { accountType: offered, reason } }
}
